import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  CheckCircle2,
  Pause,
  Play,
  XCircle,
  RotateCcw,
  CreditCard,
  Clock,
  ChevronRight,
  AlertTriangle,
  Award,
} from "lucide-react";

const MEMBER_PERKS = [
  "Access to the members-only marketplace",
  "Member pricing on every beef & chicken order",
  "First access to every limited drop the moment it opens",
  "Free shipping on qualifying orders",
  "Recipes & cooking inspiration",
  "Birthday reward eligibility",
];

function statusBadge(status) {
  const map = {
    active: { label: "Active", bg: "rgba(26,67,49,0.1)", fg: "var(--ff-primary)" },
    trial: { label: "Trial", bg: "rgba(244,162,97,0.18)", fg: "#B5651A" },
    paused: { label: "Paused", bg: "rgba(232,122,93,0.18)", fg: "#B5451B" },
    cancelled: { label: "Canceled", bg: "rgba(220,38,38,0.12)", fg: "#9F1239" },
    canceled: { label: "Canceled", bg: "rgba(220,38,38,0.12)", fg: "#9F1239" },
    expired: { label: "Expired", bg: "rgba(140,140,140,0.18)", fg: "#5C5C5C" },
    none: { label: "None", bg: "rgba(140,140,140,0.18)", fg: "#5C5C5C" },
  };
  return map[status] || map.none;
}

function ConfirmModal({ open, onClose, onConfirm, title, body, confirmLabel, danger, busy }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="ms-confirm-modal"
    >
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl max-w-md w-full p-7 shadow-2xl">
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
            style={{
              background: danger ? "rgba(220,38,38,0.12)" : "rgba(244,162,97,0.18)",
              color: danger ? "#9F1239" : "#B5651A",
            }}
          >
            <AlertTriangle size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-2xl text-[var(--ff-text-primary)]">
              {title}
            </h3>
            <p className="mt-3 text-sm text-[var(--ff-text-secondary)] leading-relaxed">
              {body}
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            data-testid="ms-confirm-cancel"
          >
            Never mind
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="ms-confirm-go"
            className={`inline-flex items-center justify-center px-5 py-2.5 rounded-full font-semibold text-white transition-all ${
              danger
                ? "bg-rose-700 hover:bg-rose-800"
                : "bg-[var(--ff-primary)] hover:bg-[var(--ff-primary-hover)]"
            } disabled:opacity-60`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MembershipSettings({ membership, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(null); // "pause" | "cancel" | null

  const snoozedUntil = membership?.snoozed_until && new Date(membership.snoozed_until);
  const isPaused = !!(snoozedUntil && snoozedUntil > new Date());
  const cancelledAt = !!membership?.cancelled_at;
  const autoRenew = membership?.auto_renew !== false;

  // Derived status (display only)
  let displayStatus = membership?.status || "none";
  if (isPaused) displayStatus = "paused";
  else if (cancelledAt || !autoRenew) displayStatus = "cancelled";

  const badge = statusBadge(displayStatus);
  const perks = MEMBER_PERKS;
  const isAnnual =
    (membership?.plan_name || "").toLowerCase().includes("annual") ||
    (membership?.plan_id || "").includes("yearly") ||
    (membership?.plan_id || "").includes("annual");

  const noMembership =
    !membership ||
    membership.status === "none" ||
    (!membership.status && !membership.plan_name);

  const callApi = async (path, body, successMsg) => {
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const { data } = await api.post(path, body || {});
      setMsg((data && data.message) || successMsg);
      setTimeout(() => setMsg(""), 7000);
      onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Action failed. Please try again.");
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/memberships/portal", {
        return_url: window.location.origin + "/account",
      });
      if (data.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setErr(data.message || "Billing portal not available yet.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not open portal.");
    } finally {
      setBusy(false);
    }
  };

  if (noMembership) {
    return (
      <div className="card-flat p-8" data-testid="ms-empty">
        <h3 className="font-display text-3xl">No active membership</h3>
        <p className="mt-3 text-[var(--ff-text-secondary)] leading-relaxed max-w-xl">
          The Farm To You is a members-only platform. Join to unlock limited
          drops, curated boxes, free shipping, and the full member experience.
        </p>
        <Link to="/pricing" className="btn-primary mt-6 inline-flex">
          View plans
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="membership-settings">
      <div>
        <p className="overline">Membership Settings</p>
        <h2 className="font-display text-3xl mt-1">Manage your membership.</h2>
      </div>

      {/* Main settings card */}
      <div className="rounded-2xl border border-[var(--ff-border)] bg-white overflow-hidden">
        {/* Header */}
        <div
          className="px-6 lg:px-8 py-6 lg:py-7 border-b border-[var(--ff-border)]"
          style={{ background: "linear-gradient(180deg, #FAF7F1 0%, #FFFFFF 100%)" }}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-secondary)]">
                Membership
              </p>
              <h3 className="mt-2 font-display text-3xl text-[var(--ff-text-primary)]">
                {membership.plan_name || "The Farm To You"}
              </h3>
            </div>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: badge.bg, color: badge.fg }}
              data-testid="ms-status-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: badge.fg }} />
              {badge.label}
            </span>
          </div>
          {(membership?.comp || membership?.source === "ambassador_comp") && (
            <span
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: "rgba(244,162,97,0.16)", color: "#B5651A" }}
              data-testid="ms-comp-badge"
            >
              <Award size={13} /> Ambassador — complimentary
            </span>
          )}

          {/* Status-specific note */}
          {isPaused && (
            <p className="mt-3 text-sm text-[var(--ff-text-secondary)]" data-testid="ms-paused-note">
              Paused until{" "}
              <strong>{snoozedUntil.toLocaleDateString()}</strong>. Member
              benefits resume automatically when the pause ends.
            </p>
          )}
          {!isPaused && cancelledAt && (
            <p className="mt-3 text-sm text-[var(--ff-text-secondary)]" data-testid="ms-cancelled-note">
              Canceled. Your benefits remain active through{" "}
              <strong>
                {membership.renews_at
                  ? new Date(membership.renews_at).toLocaleDateString()
                  : "your current period"}
              </strong>
              . Reactivate anytime before then to keep your membership running.
            </p>
          )}
        </div>

        {/* Detail grid */}
        <div className="px-6 lg:px-8 py-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="ms-details">
          <Detail label="Renews on" testid="ms-renews-at">
            {membership.renews_at
              ? new Date(membership.renews_at).toLocaleDateString()
              : "—"}
          </Detail>
          <Detail label="Billing frequency" testid="ms-billing-freq">
            {membership.plan_name?.toLowerCase().includes("annual") ||
            (membership.plan_id || "").includes("yearly") ||
            (membership.plan_id || "").includes("annual")
              ? "Annual"
              : "Monthly"}
          </Detail>
          <Detail label="Auto-renewal" testid="ms-auto-renew">
            {autoRenew && !cancelledAt ? "On" : "Off"}
          </Detail>
        </div>

        {/* Perks */}
        <div className="px-6 lg:px-8 py-6 border-t border-[var(--ff-border)]" data-testid="ms-perks">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-secondary)]">
            Perks included
          </p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-y-2 gap-x-6">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-[var(--ff-text-secondary)]">
                <CheckCircle2 size={14} className="text-[var(--ff-primary)] mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <div className="px-6 lg:px-8 py-6 border-t border-[var(--ff-border)] flex flex-wrap gap-3 items-center" data-testid="ms-actions">
          {/* Payment method */}
          <button
            type="button"
            onClick={openPortal}
            disabled={busy}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="ms-billing-portal"
          >
            <CreditCard size={14} /> Manage payment method
          </button>

          {/* Pause / Resume */}
          {isPaused ? (
            <button
              type="button"
              onClick={() => callApi("/memberships/resume", null, "Membership resumed.")}
              disabled={busy}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="ms-resume"
            >
              <Play size={14} /> Resume membership
            </button>
          ) : !cancelledAt ? (
            <button
              type="button"
              onClick={() => setConfirm("pause")}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[var(--ff-primary)] border border-[var(--ff-primary)] hover:bg-[var(--ff-primary)] hover:text-white transition-colors"
              data-testid="ms-pause"
            >
              <Pause size={14} /> Pause membership
            </button>
          ) : null}

          {/* Cancel / Reactivate */}
          {cancelledAt ? (
            <button
              type="button"
              onClick={() => callApi("/memberships/reactivate", null, "Membership reactivated.")}
              disabled={busy}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="ms-reactivate"
            >
              <RotateCcw size={14} /> Reactivate membership
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirm("cancel_save")}
              disabled={busy || isPaused}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-rose-700 border border-rose-200 hover:bg-rose-50 transition-colors disabled:opacity-50"
              data-testid="ms-cancel"
            >
              <XCircle size={14} /> Cancel membership
            </button>
          )}
        </div>

        {(msg || err) && (
          <div className="px-6 lg:px-8 pb-6">
            {msg && (
              <p className="text-sm text-[var(--ff-primary)]" data-testid="ms-msg">
                {msg}
              </p>
            )}
            {err && (
              <p className="text-sm text-rose-700" data-testid="ms-err">
                {err}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Pause copy callout */}
      {!isPaused && !cancelledAt && (
        <div className="rounded-2xl p-5 border border-[var(--ff-border)] bg-[var(--ff-muted)]">
          <p className="text-sm text-[var(--ff-text-secondary)] flex items-start gap-2">
            <Clock size={14} className="text-[var(--ff-secondary)] mt-1 shrink-0" />
            <span>
              <strong>Need a break?</strong> Pause your membership anytime and
              come back when you're ready. You can also cancel anytime — your
              benefits remain active until the end of your current billing
              period.
            </span>
          </p>
        </div>
      )}

      <p className="text-xs text-[var(--ff-text-muted)] italic max-w-2xl">
        Manage everything from your account portal. <Link to="/contact" className="underline">Contact support</Link> if you need help.
      </p>

      <ConfirmModal
        open={confirm === "pause"}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          callApi("/memberships/snooze", { weeks: 4 }, "Membership paused.")
        }
        title="Pause your membership?"
        body="Are you sure you want to pause your membership? While paused, you will not have access to member-only drops or active membership perks. You can resume anytime."
        confirmLabel="Pause for 4 weeks"
        busy={busy}
      />
      <ConfirmModal
        open={confirm === "cancel"}
        onClose={() => setConfirm(null)}
        onConfirm={() => callApi("/memberships/cancel", null, "Membership cancellation scheduled.")}
        title="Cancel your membership?"
        body={
          isAnnual
            ? "Annual memberships are non-refundable and not prorated. Your benefits stay active through the end of your paid year, then your membership simply won't renew."
            : "Monthly memberships require a 30-day written notice — one more monthly charge will be processed, then your membership ends. You'll also need to mail written notice to: The Farm To You, Canceling Department, PO Box 2, Oldsmar, FL 34677-0002, USA. If your mail carrier does not accept PO Boxes, you may instead mail it to: The Farm To You, Canceling Department, 3905 Tampa Rd, Unit 2, Oldsmar, FL 34677-3125, USA."
        }
        confirmLabel="Yes, cancel"
        danger
        busy={busy}
      />

      {/* Cancel-save flow — offer Pause before final cancel */}
      {confirm === "cancel_save" && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => !busy && setConfirm(null)}
          data-testid="cancel-save-modal"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="overline text-[var(--ff-primary)]">Before you go…</p>
            <h3 className="font-display text-3xl mt-3 text-[var(--ff-primary)] leading-tight">
              Pause for a month instead?
            </h3>
            <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed">
              Life happens. Pause your membership for 30 days — we&apos;ll hold your founding seat, skip your next billing cycle, and start you back up when you&apos;re ready. No commitment.
            </p>
            <div className="mt-8 space-y-2">
              <button
                type="button"
                onClick={() => callApi("/memberships/pause", null, "Membership paused for 30 days.")}
                disabled={busy}
                className="w-full btn-primary inline-flex items-center justify-center gap-2 py-3"
                data-testid="cancel-save-pause"
              >
                <Pause size={15}/> Pause for 30 days instead
              </button>
              <button
                type="button"
                onClick={() => setConfirm("cancel")}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-rose-700 hover:bg-rose-50 transition-colors"
                data-testid="cancel-save-continue-cancel"
              >
                No thanks, cancel anyway
              </button>
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-[var(--ff-text-muted)] hover:text-[var(--ff-primary)] transition-colors"
                data-testid="cancel-save-nevermind"
              >
                Never mind, keep my membership
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-text-muted)]">
        {label}
      </p>
      <p className="mt-1.5 text-base text-[var(--ff-text-primary)] font-medium">
        {children}
      </p>
    </div>
  );
}
